<!-- Page-level loading progress indicator -->
<mat-progress-bar *ngIf="isLoading" mode="indeterminate" color="primary"
  class="page-progress"></mat-progress-bar>

<div class="page-container">

  <!-- ── Info banner ── -->
  <div class="org-banner" *ngIf="bannerVisible">
    <mat-icon class="org-banner-icon">info_outline</mat-icon>
    <div class="org-banner-text">
      <strong>Organisation-level template subscriptions</strong>
      <p>
        These Azure subscriptions are used for testing VM templates, or other resources without needing a Requisition or a class.
        This allows independently tracking usage for organization scoped templates and allows validating the template system before hand.
        These are explicitly distinct from adding Class specific Subscriptions, and as-such resources created in these subscriptions have no bearing on class-specific quotas or allocations.
      </p>
    </div>
    <div class="org-banner-actions">
      <mat-checkbox [(ngModel)]="bannerDismissForever" class="banner-dismiss-check">
        Don't show again
      </mat-checkbox>
      <button mat-icon-button class="banner-close-btn"
              (click)="dismissBanner()"
              matTooltip="Dismiss">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- ── Search bar ── -->
  <mat-form-field appearance="outline" class="sub-search-field">
    <mat-label>Search subscriptions</mat-label>
    <mat-icon matPrefix>search</mat-icon>
    <input matInput [(ngModel)]="searchTerm" (input)="onSearchChange()"
           placeholder="Name, subscription ID, or region…">
    <button *ngIf="searchTerm" matSuffix mat-icon-button (click)="searchTerm=''; onSearchChange()">
      <mat-icon>close</mat-icon>
    </button>
  </mat-form-field>

  <!-- ── Section header ── -->
  <div class="section-header-row">
    <div class="section-header-title">
      <h3 class="section-title">Subscriptions</h3>
      <span *ngIf="!isLoading" class="section-count">{{ dataSource.data.length }} configured</span>
    </div>
    <div class="section-header-actions">
      <button mat-stroked-button
        (click)="refreshAllQuota()"
        [disabled]="isRefreshingAll || isLoading || dataSource.data.length === 0"
        matTooltip="Refresh all quotas from Azure">
        <mat-spinner *ngIf="isRefreshingAll" diameter="16"
          style="display:inline-block;margin-right:6px;vertical-align:middle;"></mat-spinner>
        <mat-icon *ngIf="!isRefreshingAll">refresh</mat-icon>
        Refresh Quotas
      </button>
      <button mat-raised-button color="primary" (click)="openBulkAddDialog()">
        <mat-icon>add</mat-icon>
        Add Subscriptions
      </button>
    </div>
  </div>

  <!-- ── Empty state ── -->
  <div *ngIf="!isLoading && dataSource.data.length === 0" class="empty-state">
    <mat-icon class="empty-icon">cloud_off</mat-icon>
    <p>No template subscriptions configured.</p>
  </div>

  <!-- ── Table ── -->
  <div *ngIf="dataSource.data.length > 0" class="table-wrapper mat-elevation-z2">
    <table mat-table [dataSource]="dataSource" class="sub-table">

      <!-- Display Name -->
      <ng-container matColumnDef="displayName">
        <th mat-header-cell *matHeaderCellDef>Subscription</th>
        <td mat-cell *matCellDef="let row">
          <div class="sub-name-cell">
            <strong>{{ row.SubscriptionDisplayName || 'Unnamed' }}</strong>
            <span class="sub-location-label">{{ row.Location || '—' }}</span>
          </div>
        </td>
      </ng-container>

      <!-- Subscription ID -->
      <ng-container matColumnDef="subscriptionId">
        <th mat-header-cell *matHeaderCellDef>Subscription ID</th>
        <td mat-cell *matCellDef="let row">
          <span class="mono-text" [matTooltip]="row.SubscriptionId">
            {{ row.SubscriptionId }}
          </span>
        </td>
      </ng-container>

      <!-- Location -->
      <ng-container matColumnDef="location">
        <th mat-header-cell *matHeaderCellDef>Region</th>
        <td mat-cell *matCellDef="let row">{{ row.Location || '—' }}</td>
      </ng-container>

      <!-- Quota (Cores) -->
      <ng-container matColumnDef="quota">
        <th mat-header-cell *matHeaderCellDef>Quota (Cores)</th>
        <td mat-cell *matCellDef="let row">
          <div *ngIf="row.CoresLimit !== undefined && row.CoresLimit !== null; else noQuota"
            class="quota-cell">
            <div class="quota-numbers">
              <span class="quota-used">{{ row.CoresCurrentUsage ?? 0 }}</span>
              <span class="quota-sep">/</span>
              <span class="quota-limit">{{ row.CoresLimit }}</span>
              <span class="quota-pct">({{ quotaUsagePercent(row) }}%)</span>
              <span *ngIf="row.PendingCores" class="quota-pending"
                matTooltip="Cores pending provisioning">
                &bull; {{ row.PendingCores }} pending
              </span>
              <mat-icon *ngIf="getFamilyTooltip(row)"
                class="quota-family-icon"
                [matTooltip]="getFamilyTooltip(row)"
                matTooltipClass="quota-family-tooltip">
                info_outline
              </mat-icon>
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="quotaUsagePercent(row)"
              [style.--mdc-linear-progress-active-indicator-color]="quotaBarColor(row)"
              [style.--mdc-linear-progress-track-color]="'#e0e0e0'"
              class="quota-bar">
            </mat-progress-bar>
            <span class="quota-ts">Checked: {{ formatQuotaCheckedAt(row.QuotaCheckedAt) }}</span>
          </div>
          <ng-template #noQuota>
            <ng-container *ngIf="row.ProviderRegistrationStatus === 'registering'; else dashOnly">
              <span class="provider-registering-badge"
                matTooltip="Azure resource providers are being registered for this subscription. Refresh in a few minutes to view quota."
                matTooltipPosition="right">
                <mat-icon class="registering-icon">hourglass_empty</mat-icon>
                Registering providers…
              </span>
            </ng-container>
            <ng-template #dashOnly><span class="text-muted">—</span></ng-template>
          </ng-template>
        </td>
      </ng-container>

      <!-- Actions -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let row">
          <div class="row-actions">
            <button mat-icon-button
              matTooltip="Refresh quota"
              [disabled]="row.isRefreshing"
              (click)="refreshRowQuota(row)">
              <mat-spinner *ngIf="row.isRefreshing" diameter="18"></mat-spinner>
              <mat-icon *ngIf="!row.isRefreshing">sync</mat-icon>
            </button>
            <button mat-icon-button color="warn"
              matTooltip="Delete subscription"
              (click)="removeSubscription(row)">
              <mat-icon>delete_outline</mat-icon>
            </button>
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="sub-row"></tr>
    </table>

    <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageSize]="pageSize" appPersistPageSize="templateSubscriptions:list"
      showFirstLastButtons aria-label="Select page"></mat-paginator>
  </div>

</div>

